import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { COMPARE_PLANS_PATH, RestrictionLockedDialog } from './LockedFeatureDialog';
import { restrictionMessage, type SubscriptionRestriction } from './subscriptionRestriction';

/**
 * What a screen shows when the server refused an action with `SUBSCRIPTION_UPGRADE_REQUIRED`: the server's own explanation in the page (so it
 * survives closing the dialog), the shared locked-feature dialog opened over it, and a Compare plans link. It never replaces a screen with a
 * blank or generic error and never performs an upgrade. Everything the person entered on the screen stays where it is: this component only
 * adds an explanation beside it.
 *
 * Pass the refusal object itself: showing a NEW refusal opens the dialog again, while closing it keeps the message in the page.
 * `children` are caller-specific actions shown with the message (for example a way to carry on without the gated part).
 */
export function UpgradeRefusalNotice({
  restriction, fallbackFeatureName, nothingChanged = 'Nothing was changed.', children,
}: {
  restriction: SubscriptionRestriction;
  fallbackFeatureName: string;
  nothingChanged?: string;
  children?: ReactNode;
}) {
  const [dismissed, setDismissed] = useState<SubscriptionRestriction | null>(null);
  const dialogOpen = dismissed !== restriction;
  return (
    <>
      <div className="jr-error jr-error--block" role="alert" data-testid="upgrade-refusal">
        <p>{restrictionMessage(restriction)}</p>
        <p className="muted">
          {nothingChanged} Only your company administrator can change the plan. <Link to={COMPARE_PLANS_PATH}>Compare plans</Link>
        </p>
        {children}
      </div>
      {dialogOpen && (
        <RestrictionLockedDialog restriction={restriction} fallbackFeatureName={fallbackFeatureName} onClose={() => setDismissed(restriction)} />
      )}
    </>
  );
}
